import * as CL from '@design-system/component-library';
import * as React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { JsonLd } from 'react-schemaorg';
import { dsClass } from '../../common/constants/dsClasses.js';
import { t } from '../../common/i18n/index.js';
import { createProductStructuredDataFromOnlineModel } from '../../common/structured-data/product.js';
import { isSquareTradeAddOn } from '../../common/utils/addOnUtils.js';
import { userHasEmptyOrSMEPriceGroup } from '../../common/utils/employeeUtils.js';
import { clampQuantity } from '../../common/utils/quantityUtils.js';
import { useAuth } from '../../public/site/AuthProvider';
import { SiteContext } from '../../public/site/SiteContext.js';
import { Loading } from '../Loading/index.js';
import { ProductDetailsAddOn } from './partials/ProductDetailsAddOn.js';
import { ProductDetailsAvailability } from './partials/ProductDetailsAvailability.js';
import { ProductDetailsDescription } from './partials/ProductDetailsDescription.js';
import { ProductDetailsDiscontinued } from './partials/ProductDetailsDiscontinued.js';
import { ProductDetailsOffer } from './partials/ProductDetailsOffer.js';
import { ProductDetailsPayment } from './partials/ProductDetailsPayment.js';
import { ProductDetailsPicture } from './partials/ProductDetailsPicture.js';
import { ProductDetailsPriceDisclaimer } from './partials/ProductDetailsPriceDisclaimer.js';
import { ProductDetailsQuantity } from './partials/ProductDetailsQuantity.js';
import { ProductDetailsSummary } from './partials/ProductDetailsSummary.js';
import { DEFAULT_QUANTITY, PRODUCT_DETAILS_CLASS } from './productDetailsConstants.js';
import {
  assemblePictures,
  calculatePayments,
  filterAndSortOffersByAvailability,
  filterAndSortOffersByName,
  getAddOnsAndDefaultValues,
  getDefaultPaymentTerm,
  getOfferPictures,
  isCommercialProductPaymentAllowed,
  onClickAddToCart,
  selectDefaultCommercialProduct,
  useProcessOnlineModel,
  valueOrZero,
} from './utils/productDetailsUtils.js';
import type { ProductDetailsAddOnStateProperties } from './partials/ProductDetailsAddOn.js';
import type { ProductDetailsDescriptionProps } from './partials/ProductDetailsDescription.js';
import type { PictureData } from './partials/ProductDetailsPicture.js';
import type {
  AddOn,
  AddOnPurpose,
  Catalog,
  CommercialProduct,
  CompanyInfoResponse,
  Offer,
  OnlineModel,
} from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { AdditionalField } from '../../selfservice/common/shopping-cart/shoppingCartFunctions.js';
import type { ECommerceEvents } from '../PublicPage/types.js';
import type { Product } from 'schema-dts';

import './ProductDetails.scss';

export interface ProductDetailsProps extends ProductDetailsPropsWithShoppingCart {
  showOfferAndPrices: boolean;
}

export interface ProductDetailsPropsWithShoppingCart extends ProductDetailsDescriptionProps {
  addToCart: (cartItemContainer: CartItemContainer) => void;
  eCommerceEvents?: ECommerceEvents;
  isPublicSite: boolean;
  isEmployee: boolean;
  companyInfo?: CompanyInfoResponse;
  catalog?: Catalog;
  user?: AuthenticatedUser;
}

export interface ProductDetailsComponentProps extends ProductDetailsProps {
  addOns: AddOn[];
  offers: Offer[];
  pictures: PictureData[];
  isYtt?: boolean;
}

export interface ProductDetailsComponentRightSideProps extends ProductDetailsComponentProps {
  offer: Offer;
  setOffer: (offer: Offer) => void;
}

export interface ProductDetailsPropsComponentBottomRightSide extends ProductDetailsPropsWithShoppingCart {
  commercialProduct: CommercialProduct;
  productQuantity: number;
  offer: Offer;
  addOns: AddOn[];
}

export interface CartItemContainer {
  onlineModel: OnlineModel;
  offer: Offer;
  commercialProduct: CommercialProduct;
  quantity: number;
  isPublicSite: boolean;
  selectedAddOnBundles: SelectedAddOnBundle[];
  additionalFields: AdditionalField[];
  productPath: string;
}

export interface SelectedAddOnBundle {
  addOnAssociationCode: string;
  addOnAssociationId: string;
  addOnPurpose: AddOnPurpose;
  display: boolean;
  addOnCode: string;
  count: number;
  isMandatory: boolean;
}

const YTT_DEFAULT_SELECTED_OFFER_NAME = 'Elisa Yritystietoturva 3 laitteeseen 12 kk laskutusväli';

const mergeAddOnSelections = (
  updatedAddOns: ProductDetailsAddOnStateProperties[],
  previousAddOns: ProductDetailsAddOnStateProperties[]
): ProductDetailsAddOnStateProperties[] => {
  const previousByCode = new Map(previousAddOns.map(addOn => [addOn.addOn.addOnCode, addOn]));
  return updatedAddOns.map(addOn => {
    const previous = previousByCode.get(addOn.addOn.addOnCode);
    return previous ? { ...addOn, isChecked: previous.isChecked, quantity: previous.quantity } : addOn;
  });
};

const ProductDetailsComponentBottomRightSide = (props: ProductDetailsPropsComponentBottomRightSide) => {
  const { isEmployee, commercialProduct, companyInfo, catalog, productQuantity, offer, addOns, user } = props;
  const { pathname } = useLocation();
  const eppCategories = isEmployee
    ? catalog?.damageInsurance
    : companyInfo?.eppSolution?.damageInsuranceDeviceCategories;
  const [addOnsState, setAddOnsState] = useState<ProductDetailsAddOnStateProperties[]>(
    getAddOnsAndDefaultValues(
      addOns,
      commercialProduct,
      eppCategories,
      isEmployee,
      productQuantity,
      offer.eppCategory,
      user
    )
  );

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    setAddOnsState(previousAddOns =>
      previousAddOns.map(addOn => ({
        ...addOn,
        quantity: productQuantity,
      }))
    );
  }, [productQuantity]);

  // eppCategories (from companyInfo.eppSolution) and commercialProduct.addOnAssociations can both change
  // asynchronously after this component's initial mount (e.g. once companyInfo finishes loading on a
  // fresh session), so the add-on list must be recomputed when either changes. The `key` prop on this
  // component (based on commercialProductId/Name) does not always change in that case, so it cannot be
  // relied upon alone to force a remount.
  useEffect(() => {
    const updatedAddOns = getAddOnsAndDefaultValues(
      addOns,
      commercialProduct,
      eppCategories,
      isEmployee,
      productQuantity,
      offer.eppCategory,
      user
    );
    setAddOnsState(previousAddOns => mergeAddOnSelections(updatedAddOns, previousAddOns));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eppCategories, commercialProduct]);

  return (
    <>
      <ProductDetailsAddOn addOns={addOnsState} setStatus={setAddOnsState} productQuantity={productQuantity} />
      <ProductDetailsSummary
        commercialProduct={commercialProduct}
        offer={offer}
        addToCart={() => onClickAddToCart(props, addOnsState, decodeURI(pathname), isAuthenticated)}
        quantity={productQuantity}
        isEmployee={isEmployee}
        addOns={addOnsState}
      />
      {isEmployee ? <ProductDetailsPriceDisclaimer /> : null}
    </>
  );
};

const ProductDetailsComponentRightSide = ({
  onlineModel,
  offers,
  addToCart,
  isPublicSite,
  isEmployee,
  companyInfo,
  catalog,
  addOns,
  offer,
  setOffer,
  user,
}: ProductDetailsComponentRightSideProps) => {
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  // Any other group than SME means that recurring charges for non-EPP are disabled if not explicitly allowed
  const commercialProducts = offer.commercialProducts.filter(cp =>
    isCommercialProductPaymentAllowed(cp, user?.segmentPricingGroup, companyInfo?.eppSolution)
  );
  const [commercialProduct, setCommercialProduct] = useState<CommercialProduct>(
    selectDefaultCommercialProduct(commercialProducts, onlineModel.category)
  );
  const [paymentMethod, setPaymentMethod] = useState(commercialProduct?.payments ?? 0);
  const [subType, setSubType] = useState(commercialProduct?.productSubType ?? '');
  // `commercialProducts` can be recomputed with a larger/different set once companyInfo/eppSolution
  // finishes loading asynchronously (e.g. EPP-specific products get added to the offer). Until the user
  // has manually chosen a payment plan, keep re-applying the EPP-preferring default so the outcome
  // doesn't depend on the race between this component's initial mount and that async data arriving.
  const hasUserSelectedProduct = useRef(false);
  const handleCommercialProductSelected = (cp: CommercialProduct) => {
    hasUserSelectedProduct.current = true;
    setCommercialProduct(cp);
  };

  const payments = calculatePayments(
    commercialProduct,
    offer,
    quantity,
    getAddOnsAndDefaultValues(
      addOns,
      commercialProduct,
      companyInfo?.eppSolution?.damageInsuranceDeviceCategories || catalog?.damageInsurance,
      isEmployee,
      quantity,
      offer.eppCategory
    ),
    isEmployee
  );
  const filteredAddons = userHasEmptyOrSMEPriceGroup(user?.segmentPricingGroup)
    ? addOns
    : addOns.filter(addon => !isSquareTradeAddOn(addon));
  useEffect(() => {
    // Prefer re-selecting the exact same product by id first: `commercialProducts` can be recomputed
    // with a larger/different set (e.g. once companyInfo/eppSolution finishes loading and EPP-specific
    // products are added to the offer), and matching purely by payments/subType/charges can then
    // accidentally cross-match a different, newly-added product that coincidentally has the same charges.
    // Before the user has manually picked a plan, re-apply the EPP-preferring default instead, so a
    // late-arriving EPP option isn't skipped just because it wasn't present on the very first render.
    const selectedProduct = hasUserSelectedProduct.current
      ? commercialProducts.find(p => p.commercialProductId === commercialProduct.commercialProductId) ||
        commercialProducts.find(
          p =>
            paymentMethod === p.payments &&
            p.productSubType === subType &&
            p.monthlyRecurringCharge === commercialProduct.monthlyRecurringCharge &&
            p.oneTimeCharge === commercialProduct.oneTimeCharge
        ) ||
        commercialProducts[0]
      : selectDefaultCommercialProduct(commercialProducts, onlineModel.category);
    setPaymentMethod(selectedProduct.payments || getDefaultPaymentTerm(onlineModel.category));
    setSubType(selectedProduct?.productSubType ?? '');
    setCommercialProduct(selectedProduct);
  }, [commercialProducts]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <ProductDetailsOffer
        offers={offers}
        offer={offer}
        setOffer={setOffer}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        onlineModelCategory={onlineModel.category}
      />
      {isEmployee && (
        <div className={`${dsClass.MARGIN_TOP_2} ${dsClass.MARGIN_BOTTOM_5}`}>
          {valueOrZero(payments.monthlyRecurringCharge) === 0 && valueOrZero(payments.oneTimeCharge) === 0 ? (
            <span className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}>
              <span className={dsClass.MARGIN_TOP_1}>
                <CL.Icon icon="check" size="m" type="filled" color="white--dark" />
              </span>
              <span className={dsClass.MARGIN_LEFT_2}> {t.E12Y('Your employer entirely pays for this device')}</span>
            </span>
          ) : (
            <span className={dsClass.DISPLAY_FLEX}>
              <span className={dsClass.MARGIN_TOP_1}>
                {' '}
                <CL.Icon icon="information" size="m" type="regular" color="white--dark" />
              </span>
              <span className={dsClass.MARGIN_LEFT_2}>
                {t.EYS6(
                  'Your employer does not entirely pay for this device, but you can order it by paying the presented monthly charge yourself.'
                )}
              </span>
            </span>
          )}
        </div>
      )}
      {!(isEmployee && commercialProducts.length < 2) && (
        <ProductDetailsPayment
          commercialProducts={commercialProducts}
          commercialProduct={commercialProduct}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          subType={subType}
          setSubType={setSubType}
          quantity={quantity}
          setCommercialProduct={handleCommercialProductSelected}
          onlineModelCategory={onlineModel.category}
          eppSolution={companyInfo?.eppSolution}
          segmentPricingGroup={user?.segmentPricingGroup}
          isEmployee={isEmployee}
        />
      )}
      <ProductDetailsQuantity quantity={quantity} setQuantity={(q: number) => setQuantity(clampQuantity(q))} />
      <div className={dsClass.MARGIN_BOTTOM_5}>
        <ProductDetailsAvailability offer={offer} />
      </div>
      {/* This is for Frosmo to place compact cards */}
      <div id="product-details-card-placeholder" />
      <ProductDetailsComponentBottomRightSide
        key={`${commercialProduct.commercialProductId}-${commercialProduct.commercialProductName}`}
        commercialProduct={commercialProduct}
        productQuantity={quantity}
        offer={offer}
        addToCart={addToCart}
        isPublicSite={isPublicSite}
        isEmployee={isEmployee}
        onlineModel={onlineModel}
        companyInfo={companyInfo}
        catalog={catalog}
        addOns={filteredAddons}
        user={user}
      />
    </>
  );
};

function getYttDefaultOffer(offers: Offer[]) {
  return offers.find(o => o.offerName.toUpperCase().trim() === YTT_DEFAULT_SELECTED_OFFER_NAME.toUpperCase().trim());
}

export const ProductDetailsComponent = (props: ProductDetailsComponentProps) => {
  const {
    companyInfo,
    eCommerceEvents,
    isPublicSite,
    offers,
    onlineModel,
    pictures,
    showOfferAndPrices,
    isYtt = false,
  } = props;
  const { siteBaseUrl } = useContext(SiteContext);

  // For YTT, we want certain offer as default if available
  const defaultOffer = isYtt ? getYttDefaultOffer(offers) || offers[0] : offers[0];
  const [offer, setOffer] = useState<Offer>(defaultOffer);

  React.useEffect(() => {
    if (eCommerceEvents?.onSeeProduct && offers.length) {
      eCommerceEvents?.onSeeProduct([onlineModel], [offers]);
    }
  }, []); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  // Sets the offer when it updates in props, without this offer stays as put in useState
  // Fixes OFI-33815 EPP prices not showing.
  React.useEffect(() => {
    setOffer(defaultOffer);
  }, [defaultOffer]);

  const eppSolutionStatus = companyInfo?.eppSolution?.eppSolutionStatus || '';
  const offersWithCommercialProducts = props.onlineModel.offers.length > 0;
  const isAvailable = Boolean(offers.length);
  const structuredData = createProductStructuredDataFromOnlineModel(onlineModel, siteBaseUrl);

  const getCurrentOfferPictures = () => {
    if (!offer) {
      return [];
    }

    return getOfferPictures(pictures, offer);
  };

  return (
    <div className={PRODUCT_DETAILS_CLASS} data-online-model={onlineModel.onlineModelCode}>
      <CL.Grid>
        <CL.GridRow>
          <CL.GridCol colWidthL={7} colWidthM={6} colWidthS={6} colWidthXS={4}>
            <ProductDetailsPicture key={offer?.offerId} pictures={getCurrentOfferPictures()} offer={offer} />
          </CL.GridCol>
          <CL.GridCol colWidthL={5} colWidthM={6} colWidthS={6} colWidthXS={4}>
            <ProductDetailsDescription
              onlineModel={onlineModel}
              isPublicSite={isPublicSite}
              isAvailable={isAvailable}
            />
            <ProductDetailsDiscontinued
              isAvailable={isAvailable}
              eppSolutionStatus={eppSolutionStatus}
              offersWithCommercialProducts={offersWithCommercialProducts}
            />
            {isAvailable &&
              (showOfferAndPrices ? (
                <ProductDetailsComponentRightSide {...props} {...{ offer, setOffer }} />
              ) : (
                // showOfferAndPrices is only false while private/employee pricing is still being fetched.
                <Loading />
              ))}
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
      {structuredData && <JsonLd<Product> item={structuredData} />}
    </div>
  );
};

export const ProductDetails = (props: ProductDetailsProps) => {
  const processedOnlineModel = useProcessOnlineModel(props.onlineModel, props.companyInfo, props.user, props.catalog);

  const isYtt = processedOnlineModel.onlineModelName === 'Elisa Yritystietoturva';

  const offers = useMemo(
    () =>
      isYtt
        ? filterAndSortOffersByName(processedOnlineModel.offers)
        : filterAndSortOffersByAvailability(processedOnlineModel.offers),
    [processedOnlineModel, isYtt]
  );

  const pictures = assemblePictures(offers, props.onlineModel);
  return (
    <ProductDetailsComponent
      {...props}
      addOns={processedOnlineModel.addOns || []}
      offers={offers}
      pictures={pictures}
      isYtt={isYtt}
    />
  );
};
